import { httpPost, httpPatchMerge, httpDelete } from './http'
import {
  getCompanyFromOrderMasterService,
  getSuppliersFromOrderMasterService,
} from './orderMasterServices'
import { getContactsForCompanyIdPreProcessed } from './companies'
import { getContactsForSupplierIdPreProcessed } from './suppliers'
import type { IOrderMSContact } from '~/types/order/models'
import type { TEntityId, IEntitySelectItem } from '~/types/models'
import { Contact } from '~/cynqueModules/dataObjects/Contact'

const apiEntityPath = '/api/ordermscontacts'

/**
 * Candidate contacts for the "add contact" picker on an order master service (service
 * group): the order's company contacts plus the contacts of every supplier used by that
 * master service. Both are resolved from the master service id (the msContact's parent).
 * Mirrors getAddableContactsForOpportunityMasterServicePreProcessed.
 */
export async function getAddableContactsForOrderMasterServicePreProcessed(
  masterServiceId: TEntityId
) {
  const [company, suppliersRaw] = await Promise.all([
    getCompanyFromOrderMasterService(masterServiceId),
    // (mis)typed as IOrderMasterService but returns the supplier list at runtime.
    getSuppliersFromOrderMasterService(masterServiceId),
  ])
  const suppliers = (suppliersRaw as unknown as IEntitySelectItem[]) ?? []
  const supplierIds = suppliers.map((s) => s.id)

  const [companyContacts, ...supplierContactLists] = await Promise.all([
    company?.id
      ? getContactsForCompanyIdPreProcessed(company.id)
      : Promise.resolve([]),
    ...supplierIds.map((id) => getContactsForSupplierIdPreProcessed(id)),
  ])
  // Re-run generic Contact() so the picker label reads "Name | company/supplier | role".
  return [...companyContacts, ...supplierContactLists.flat()].map((c) =>
    Contact(c)
  )
}

export async function createOrderMSContact(entity: IOrderMSContact) {
  const callingMn = createOrderMSContact.name
  const apiPath = apiEntityPath
  return httpPost<IOrderMSContact>({
    callingMn,
    apiPath,
    entity,
  })
}

export async function updateOrderMSContact(
  entityData: Partial<IOrderMSContact>,
  id: TEntityId
) {
  const callingMn = updateOrderMSContact.name
  const apiPath = `${apiEntityPath}/${id}`
  type TPartialOrderMSContact = Partial<IOrderMSContact>
  return httpPatchMerge<TPartialOrderMSContact>({
    callingMn,
    apiPath,
    entityData,
  })
}

export async function deleteOrderMSContact(entityData: IOrderMSContact) {
  const callingMn = deleteOrderMSContact.name
  const apiPath = apiEntityPath
  return httpDelete<IOrderMSContact>({
    callingMn,
    apiPath,
    entityData,
  })
}
